import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { ChefHat, Home, Calendar, UtensilsCrossed, MessageSquare, User, LogOut, ShoppingCart, BookOpen, ClipboardList, ArrowLeftRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

const residentLinks = [
  { to: '/dashboard', label: 'Home', icon: Home },
  { to: '/events', label: 'Events', icon: Calendar },
  { to: '/bookings', label: 'My Bookings', icon: BookOpen },
  { to: '/messages', label: 'Messages', icon: MessageSquare },
];

const chefLinks = [
  { to: '/dashboard', label: 'Dashboard', icon: Home },
  { to: '/chef/bookings', label: 'Bookings', icon: ClipboardList },
  { to: '/chef/events', label: 'Events', icon: Calendar },
  { to: '/chef/menus', label: 'Menus', icon: UtensilsCrossed },
  { to: '/chef/grocery-list', label: 'Grocery List', icon: ShoppingCart },
  { to: '/messages', label: 'Messages', icon: MessageSquare },
];

export default function Header() {
  const { user, isDemo, switchRole, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const links = user?.role === 'chef' ? chefLinks : residentLinks;

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <header className="glass-header sticky top-0 z-50 border-b border-[#EBEBEB]/50" data-testid="app-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/dashboard" className="flex items-center gap-2.5 group" data-testid="logo-link">
            <div className="w-9 h-9 rounded-xl bg-[#E05A47] flex items-center justify-center">
              <ChefHat className="w-5 h-5 text-white" strokeWidth={1.5} />
            </div>
            <span className="font-bold text-lg tracking-tight text-[#222] font-[Manrope]">My Executive Chef</span>
          </Link>

          <nav className="hidden md:flex items-center gap-1" data-testid="main-nav">
            {links.map(({ to, label, icon: Icon }) => {
              const active = location.pathname === to;
              return (
                <Link
                  key={to}
                  to={to}
                  data-testid={`nav-${label.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-medium transition-colors ${active ? 'bg-[#E05A47]/10 text-[#E05A47]' : 'text-[#717171] hover:text-[#222] hover:bg-[#F7F7F7]'}`}
                >
                  <Icon className="w-4 h-4" strokeWidth={1.5} />
                  {label}
                </Link>
              );
            })}
          </nav>

          <div className="flex items-center gap-3">
            {isDemo && (
              <Button
                variant="outline"
                size="sm"
                onClick={switchRole}
                className="rounded-full border-[#E05A47] text-[#E05A47] hover:bg-[#E05A47]/5 text-xs gap-1.5 font-semibold"
                data-testid="demo-toggle-button"
              >
                <ArrowLeftRight className="w-3.5 h-3.5" />
                Switch to {user?.role === 'chef' ? 'Resident' : 'Chef'}
              </Button>
            )}
            <Link to="/profile" data-testid="profile-link">
              {user?.avatar ? (
                <img src={user.avatar} alt={user.name} className="w-8 h-8 rounded-full object-cover border border-[#EBEBEB]" />
              ) : (
                <div className="w-8 h-8 rounded-full bg-[#E05A47] flex items-center justify-center text-white text-xs font-bold font-[Manrope]">
                  {user?.name?.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                </div>
              )}
            </Link>
            <button onClick={handleLogout} className="text-[#717171] hover:text-[#222] p-1.5" data-testid="logout-button">
              <LogOut className="w-4 h-4" strokeWidth={1.5} />
            </button>
          </div>
        </div>

        {/* Mobile nav */}
        <nav className="md:hidden flex items-center gap-1 pb-2 overflow-x-auto" data-testid="mobile-nav">
          {links.map(({ to, label, icon: Icon }) => {
            const active = location.pathname === to;
            return (
              <Link
                key={to}
                to={to}
                className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${active ? 'bg-[#E05A47] text-white' : 'text-[#717171] bg-[#F7F7F7]'}`}
              >
                <Icon className="w-3.5 h-3.5" strokeWidth={1.5} />
                {label}
              </Link>
            );
          })}
        </nav>
      </div>
    </header>
  );
}
